/**
 * Ad tracking for Aromas de Brisa.
 *
 * Publishes custom storefront events through Shopify's analytics bus so a custom pixel
 * (Settings > Customer events) can forward them to Meta:
 * - `ab_whatsapp_click`: a direct "Pedir por WhatsApp" link was tapped (counted as Lead).
 * - `ab_scroll_view`: the visitor started scrolling the page, once per page (counted as ViewContent).
 * - `ab_search`: the visitor searched, in the search box or on the results page (counted as Search).
 * - `ab_order_form_opened` / `ab_whatsapp_order`: published by `ab-order.js` (InitiateCheckout / Purchase).
 */

/** Scrolled distance that counts as "started scrolling": a quarter of the screen height. */
const SCROLL_THRESHOLD = 0.25;
/** Pause after the last keystroke before a typed query counts as a search. */
const SEARCH_DELAY_MS = 1500;

/** Page context rendered by `sections/ab-floating-actions.liquid`. */
function pageInfo() {
  try {
    return JSON.parse(document.getElementById('AbPageInfo')?.textContent || '{}');
  } catch {
    return {};
  }
}

/**
 * @param {string} name
 * @param {Record<string, unknown>} payload
 */
export function publish(name, payload) {
  try {
    window.Shopify?.analytics?.publish?.(name, payload);
  } catch {
    // Tracking must never break ordering.
  }
}

/** A unique id per event, shared with Meta for deduplication. */
export function eventId() {
  return `ab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

if (!window.abTrackingReady) {
  window.abTrackingReady = true;

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[href*="wa.me/"]') : null;
      if (!(link instanceof HTMLAnchorElement)) return;

      const section = link.closest('.shopify-section');
      const product = link.closest('[data-ab-product-title]');
      publish('ab_whatsapp_click', {
        event_id: eventId(),
        source: section?.id?.replace('shopify-section-', '') || 'page',
        product_title: product instanceof HTMLElement ? product.dataset.abProductTitle : undefined,
        page: window.location.pathname,
      });
    },
    { capture: true }
  );

  // On wide screens Horizon scrolls `.page-wrapper` instead of the window, so listen in the
  // capture phase (scroll events don't bubble) and read whichever element scrolled.
  /** @param {Event} event */
  const onScroll = (event) => {
    const target = event.target;
    const scrolled =
      target instanceof Element ? Math.max(target.scrollTop, window.scrollY) : window.scrollY;
    if (scrolled < window.innerHeight * SCROLL_THRESHOLD) return;
    if (target instanceof Element && target.closest('dialog, [role="dialog"]')) return;

    document.removeEventListener('scroll', onScroll, true);
    const info = pageInfo();
    publish('ab_scroll_view', {
      event_id: eventId(),
      page: window.location.pathname,
      page_type: info.template,
      product: info.product,
    });
  };
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });

  let lastQuery = '';
  /** @param {string} query */
  const publishSearch = (query) => {
    const clean = query.trim();
    if (clean.length < 3 || clean.toLowerCase() === lastQuery) return;
    lastQuery = clean.toLowerCase();
    publish('ab_search', { event_id: eventId(), query: clean, page: window.location.pathname });
  };

  let searchTimer = 0;
  document.addEventListener('input', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'q') return;
    window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => publishSearch(input.value), SEARCH_DELAY_MS);
  });

  // The search results page (`/search?q=…`) counts too.
  const info = pageInfo();
  if (info.search_terms) publishSearch(info.search_terms);
}
